import { useState } from "react";
import { Filing } from "../../../models/Filing";
import { useFiling } from "../../../contexts/FilingContext";
import { Button } from "../../../design/button";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from "../../../design/dialog";
import { Input } from "../../../design/input";
import {
  Field,
  Fieldset,
  FieldGroup,
  Label,
  ErrorMessage,
} from "../../../design/fieldset";
import { SideType, sideTypes } from "../../../models/SideType";
import { Textarea } from "../../../design/textarea";
import { Select } from "../../../design/select";

interface Props {
  filing: Filing;
}

interface Errors {
  name?: string;
  description?: string;
  side?: string;
}

const EditFilingButton: React.FC<Props> = ({ filing }) => {
  const { updateFiling } = useFiling();
  const [name, setName] = useState(filing.name);
  const [filingDate, setFilingDate] = useState<string>(
    filing.filingDate
      ? new Date(filing.filingDate).toISOString().slice(0, 10)
      : "",
  );
  const [side, setSide] = useState<SideType>(filing.side);
  const [description, setDescription] = useState(filing.description);
  const [isOpen, setIsOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState<Errors>({});

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};
    if (!name) {
      newErrors.name = "Name is required.";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }
    setIsSaving(true);
    try {
      let data: Partial<Filing> = { name };
      if (filingDate) data.filingDate = new Date(filingDate);
      await updateFiling(filing._id, data);
      clearForm();
    } catch (e) {
      console.error(e);
    } finally {
      setIsSaving(false);
    }
  };

  const clearForm = () => {
    setName(filing.name);
    setErrors({});
    setIsOpen(false);
  };

  return (
    <>
      <Button type="button" onClick={() => setIsOpen(true)}>
        Edit
      </Button>
      <Dialog open={isOpen} onClose={setIsOpen}>
        <DialogTitle>Edit Filing</DialogTitle>
        <DialogBody>
          <Fieldset>
            <FieldGroup>
              <Field>
                <Label>Name</Label>
                <Input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Name"
                />
              </Field>
              <Field>
                <Label htmlFor="date">Filing Date</Label>
                <Input
                  id="date"
                  type="date"
                  value={filingDate}
                  onChange={(e) => setFilingDate(e.target.value)}
                  disabled={isSaving}
                />
              </Field>
              <Field>
                <Label>Side</Label>
                <Select
                  value={side}
                  onChange={(e) => setSide(e.target.value as SideType)}
                >
                  <option value="" disabled>
                    Select side
                  </option>
                  {Object.entries(sideTypes).map(([key, value]) => (
                    <option key={key} value={key}>
                      {value}
                    </option>
                  ))}
                </Select>
                {errors.side && <ErrorMessage>{errors.side}</ErrorMessage>}
              </Field>
              <Field>
                <Label>Description</Label>
                <Textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Description"
                />
                {errors.description && (
                  <ErrorMessage>{errors.description}</ErrorMessage>
                )}
              </Field>
            </FieldGroup>
          </Fieldset>
        </DialogBody>
        <DialogActions>
          <Button plain onClick={clearForm}>
            Cancel
          </Button>
          <Button disabled={isSaving} onClick={handleSubmit}>
            {isSaving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EditFilingButton;
